import React from 'react';
import { useParams } from 'react-router-dom';
import { useWindowSize } from 'react-use';
import { useQuery } from '@apollo/client';
import { copyFor } from 'config';
import SolarPanels from '/images/solar/single-solar-panel.svg';
import { LoadingPage } from 'components/loading';
import { ErrorPage } from 'components/errors';
import { usePlanLocation } from 'hooks/use-plan-location.hook';
import { Page, Grid, LeftColumnGridCell, RightColumnGridCell } from 'styles';
import { screenIsSmall } from 'utils/screen-is-small';
import { PageHeading } from 'components/page-heading';
import { StateSolarFarms } from 'pages/solar/components/state-solar-farms';
import { PersonalEnergyProduction } from 'pages/solar/components/personal-energy-production/personal-energy-production.component';
import { EnergyBreakdown } from 'pages/solar/components/energy-breakdown';
import { ProjectDetails } from 'pages/solar/components/project-details';
import { GET_STATE_VNM_STATS } from '../closed.api';

const getSolarCopy = copyFor('solar');
const getProjectDetailsCopy = copyFor('solar.projectDetails.closed');
const getPersonalEnergyProductionCopy = copyFor(
  'solar.personalEnergyProduction'
);

export const Default = () => {
  const { width } = useWindowSize();
  const { planId } = useParams<{ planId?: string }>();
  const location = usePlanLocation({ planId: Number(planId) });
  const stateStats = useQuery<
    GetStateVnmStatsQuery,
    GetStateVnmStatsQueryVariables
  >(GET_STATE_VNM_STATS, {
    skip: !planId,
    variables: { planId: Number(planId) || -1 },
  });
  const results = [location, stateStats];

  if (results.some(result => result.loading))
    return <LoadingPage dataTestId="solar-default-loading" />; // TODO: update loading layout once sunrise is complete
  if (
    results.some(result => result.error) ||
    !stateStats.data ||
    !location.data
  )
    return <ErrorPage />;

  const state = stateStats.data.plan.utilityAccounts[0].utility.state;
  const { stateFullName, zipCode } = location.data;
  const { id, vnmSolarProjectsStatistics } = state;

  const projectDetailProps = {
    location: getProjectDetailsCopy('location', {
      state: stateFullName,
    }),
    mdIdentifier: null,
    svgSrc: SolarPanels,
    title: getProjectDetailsCopy('title'),
  };

  const personalEnergyProductionCopy =
    getPersonalEnergyProductionCopy('closed');
  personalEnergyProductionCopy.body =
    getPersonalEnergyProductionCopy('closed.body');

  return (
    <Page small={{ padding: '0 0 24px' }}>
      <PageHeading page={getSolarCopy('heading')} />
      <Grid width="100%" gap="24px">
        <LeftColumnGridCell>
          <PersonalEnergyProduction
            billable={true}
            copy={personalEnergyProductionCopy}
          />
          {screenIsSmall(width) && <ProjectDetails {...projectDetailProps} />}
          <StateSolarFarms
            stateId={id}
            stateFullName={stateFullName}
            vnmSolarProjectsStatistics={vnmSolarProjectsStatistics}
          />
        </LeftColumnGridCell>
        <RightColumnGridCell>
          {!screenIsSmall(width) && <ProjectDetails {...projectDetailProps} />}
          <EnergyBreakdown
            zipCode={zipCode}
            small={{ margin: { top: '0px' } }}
          />
        </RightColumnGridCell>
      </Grid>
    </Page>
  );
};
